import {Calendar, DateHeaderProps, dayjsLocalizer, EventProps, HeaderProps} from 'react-big-calendar';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'dayjs/locale/it';
import React, {useState} from "react";
import {Box, styled} from "@mui/material";
import {RaccoltaCalendarEvent} from "../../types";
import EventComponent from "./event";
import ToolbarComponent from "./toolbar";
import theme from "../../constants/theme";
import DateHeaderComponent from "./date-header";
import WeekDayHeader from "./weekday-header";
import {isEventPast, scale} from "../../utils";
import {useMediaQuery} from "@mui/system";

dayjs.locale('it');
dayjs.extend(weekOfYear);

const localizer = dayjsLocalizer(dayjs);

const SafeCalendar = Calendar as unknown as React.FC<any>;

interface Props {
  events: RaccoltaCalendarEvent[]
}

const Wrapper = styled(Box)({
  width: scale(1200),
  minHeight: scale(660),
  maxHeight: scale(940),
  '& .rbc-header': {
    backgroundColor: '#0173C6',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    height: scale(40),
    border: 'none',
  },
  '& .rbc-month-view': {
    border: 'none',
    minHeight: scale(660),
    maxHeight: scale(940),
  },
  '& .rbc-month-row:last-child .rbc-day-bg:first-of-type': {
    borderBottomLeftRadius: scale(10),
  },
  '& .rbc-month-row:last-child .rbc-day-bg:last-of-type': {
    borderBottomRightRadius: scale(10),
  },
  '& .rbc-day-bg': {
    borderLeft: `${scale(0.5)} solid #707070`,
    borderRight: `${scale(0.5)} solid #707070`,
    height: scale(140),
  },
  '& .rbc-day-bg:first-of-type': {
    borderLeft: 'none',
  },
  '& .rbc-day-bg:last-of-type': {
    borderRight: 'none',
  },
  '& .rbc-month-row': {
    display: 'block !important',
    height: scale(140),
    minHeight: scale(140),
  },
  '& .rbc-show-more': {
    display: 'none !important',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100vw',
    minHeight: scale(660),
    height: '85vh',
    '& .rbc-calendar': {
      padding: '0 !important',
    },
    '& .rbc-header': {
      backgroundColor: theme.palette.white.main,
      color: theme.palette.lightGray.main,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      height: scale(40),
      borderTop: '1px solid #0173C6',
      borderBottom: '1px solid #0173C6',
    },
    '& .rbc-month-view': {
      border: 'none',
      minHeight: scale(660),
      maxHeight: scale(940),
    },
    '& .rbc-month-row:last-child .rbc-day-bg:first-of-type': {
      borderBottomLeftRadius: 0,
    },
    '& .rbc-month-row:last-child .rbc-day-bg:last-of-type': {
      borderBottomRightRadius: 0,
    },
    '& .rbc-month-row': {
      display: 'block !important',
      minHeight: scale(120),
      maxHeight: scale(170),
    },
    '& .rbc-day-bg': {
      minHeight: scale(120),
      height: '100%',
      maxHeight: scale(170),
    },
    '& .rbc-event': {
      paddingTop: '0 !important',
      paddingBottom: 0,
      paddingRight: 0,
      paddingLeft: 0,
      overflowX: 'hidden',
    },
  },
});

const calendarStyle = (isMobile: boolean) => {
  return {
    backgroundColor: 'white',
    borderRadius: isMobile ? 0 : scale(10),
    padding: scale(2),
  };
  }

const eventStyleGetter = (event: RaccoltaCalendarEvent) => {
  const eventDate = new Date(event.start);
  const eventIsPast = isEventPast(eventDate);

  //TODO caso grigio per posti esauriti
  let backgroundColor = eventIsPast ? theme.palette.lightGray.main : theme.palette.primary.main;
  let color = theme.palette.text.primary;

  return {
    style: {
      backgroundColor,
      color,
      borderRadius: scale(5),
      border: 'none',
      marginLeft: scale(4),
      marginRight: scale(5),
      marginTop: scale(1),
      marginBottom: scale(1),
      width: 'auto',
      paddingTop: scale(1),
    },
  };
};

const dayStyleGetter = (date: Date, currentDate: Date, isMobile: boolean) => {
  const startOfMonth = dayjs(currentDate).startOf('month');
  const startOfCalendar = startOfMonth.startOf('week');

  const diffInDays = dayjs(date).diff(startOfCalendar, 'day');
  const weekRow = Math.floor(diffInDays / 7);
  const isEvenRow = weekRow % 2 === 0;

  const isSameMonth = dayjs(date).month() === dayjs(currentDate).month();

  return {
    style: {
      textAlign: 'center',
      backgroundColor: isMobile
        ? (isEvenRow ? '#ffffff' : '#F7F7F7')
        : (isEvenRow ? '#F7F7F7' : '#ffffff'),
      color: isSameMonth ? '#000000' : '#999999',
      opacity: isSameMonth ? 1 : 0.5,
    },
  };
};

export default function WrapperCalendar({events}: Props) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleNavigate = (newDate: Date) => {
    const maxDate = dayjs().add(3, 'months').endOf('month').toDate();
    const minDate = dayjs().subtract(1, 'months').startOf('month').toDate();

    if (newDate > maxDate) {
      setCurrentDate(maxDate);
      return;
    }
    if (newDate < minDate) {
      setCurrentDate(minDate);
      return;
    }
    setCurrentDate(newDate);
  };

  // noinspection JSUnusedGlobalSymbols
  return (
    <Wrapper>
      <SafeCalendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        views={['month']}
        showAllEvents={true}
        defaultView="month"
        eventPropGetter={eventStyleGetter}
        dayPropGetter={(date: Date) => dayStyleGetter(date, currentDate, isMobile)}
        date={currentDate}
        onNavigate={handleNavigate}
        components={{
          toolbar: ToolbarComponent,
          event: (props: EventProps<RaccoltaCalendarEvent>) => <EventComponent day={props}/>,
          month: {
            header: (props: HeaderProps) => <WeekDayHeader {...props} currentDate={currentDate} />,
            dateHeader: (props: DateHeaderProps) => <DateHeaderComponent {...props} currentDate={currentDate} />,
          },
        }}
        style={calendarStyle(isMobile)}
      />
    </Wrapper>
  );
}
